import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactConfetti from "react-confetti";
import sidebarLogo from "../../../../assets/img/square-logo-v2.png";
import dragIcon from "../../../../assets/img/up-down-left-right-solid.svg";
import { PRESENT_SCREEN_MOCK } from "../../landingContent";

const SIDEBAR_ITEMS = ["present", "content", "tools", "settings"];

const ACTION_ICONS = ["fa-plus", "fa-random", "fa-briefcase", "fa-th-large", "fa-image", "fa-font"];

const CLASS_MARQUEE_TEXT = {
  Math: "Finish your exit ticket, turn in materials, and get ready for the next class.",
  Science: "Good morning! Check the board, find your seat, and follow today's agenda.",
  SciencePeriod3: "Welcome to 3rd period! Open your science notebook and get ready for today's forces lab.",
};

const CLASS_CONTENT_MOCK = {
  Math: {
    agendaTitle: "1st Period Agenda",
    secondaryTab: "Multi-Box",
    warmupTitle: "Warm-up Question",
    warmupPrompt: "Which is greater: 2/3 or 3/5? Draw a quick model to prove your answer.",
    slideCount: 6,
    activeSlide: 2,
    slideHeadline: "Comparing Unit Fractions",
    slideBullets: [
      "Unit fractions have 1 as the numerator.",
      "Compare the size of each part, not just the denominator.",
      "Use a model to prove which fraction is greater.",
    ],
    slideVisual: {
      label: "Fraction Bars",
      caption: "Shade 2/3 and 3/5, then compare the lengths.",
    },
  },
  Science: {
    agendaTitle: "2nd Period Agenda",
    secondaryTab: "Multi-Box",
    warmupTitle: "Warm-up Question",
    warmupPrompt: "What structures do plant and animal cells have in common?",
    warmupIllustration: "plant-cell",
    slideCount: 9,
    activeSlide: 4,
    slideHeadline: "Inside a Plant Cell",
    slideBullets: [
      "Cell wall supports and protects the plant cell.",
      "Chloroplasts capture light for photosynthesis.",
      "The vacuole stores water and helps the cell keep its shape.",
    ],
    slideVisual: {
      label: "Chloroplast",
      caption: "Green structures that turn sunlight into food for the cell.",
    },
  },
  SciencePeriod3: {
    agendaTitle: "3rd Period Agenda",
    secondaryTab: "Multi-Box",
    warmupTitle: "Warm-up Question",
    warmupPrompt: "What is the difference between speed and velocity?",
    slideCount: 8,
    activeSlide: 3,
    slideHeadline: "Newton's First Law",
    slideBullets: [
      "An object at rest stays at rest unless a force acts on it.",
      "An object in motion stays in motion unless a force acts on it.",
      "This tendency to resist changes in motion is called inertia.",
    ],
    slideVisual: {
      label: "Inertia",
      caption: "A book on a desk stays put until you push it.",
    },
  },
};

const AGENDA_STEPS = [
  { title: "Warm-up Question", status: "complete" },
  {
    title: "Partner Lab",
    status: "active",
    subSteps: [
      { title: "Set up materials", status: "active" },
      { title: "Collect data", status: "upcoming" },
    ],
  },
  { title: "Share Observations", status: "upcoming" },
  { title: "Exit Ticket", status: "upcoming" },
];

const PREVIOUS_CLASS_MOCK_STATE = {
  scheduleValue: "Math",
  marqueeText: "Finish your exit ticket, turn in materials, and get ready for the next class.",
  cueLabel: "Previous Class",
  cueTitle: "Math wrapping up",
  cueBody: "The previous class display is still on screen just before the schedule changes.",
  cueMeta: "8:59",
};

const NEXT_CLASS_PREVIEW_MOCK_STATE = {
  scheduleValue: "A Day",
  marqueeText: "Cleanup is complete. Science materials are away and the room is ready.",
  cueLabel: "Class Ending",
  cueTitle: "Science wraps",
  cueBody: "The current class finishes before the next period loads.",
  cueMeta: "9:44",
};

const PREVIOUS_CLASS_AGENDA_STEPS = [
  { title: "Number Talk", status: "complete" },
  { title: "Mini Lesson", status: "complete" },
  { title: "Independent Practice", status: "complete" },
  { title: "Exit Ticket", status: "complete" },
];

const NEXT_CLASS_AGENDA_STEP_TITLES = [
  "Warm-up Question",
  "Forces Demo",
  "Motion Graphs",
  "Exit Ticket",
];

const CLASS_PERIOD_AGENDA_STEPS = [
  { title: "Warm-up Question", durationMinutes: 5 },
  {
    title: "Partner Lab",
    durationMinutes: 20,
    subSteps: [
      { title: "Set up materials", durationMinutes: 6 },
      { title: "Collect data", durationMinutes: 14 },
    ],
  },
  { title: "Share Observations", durationMinutes: 8 },
  { title: "Exit Ticket", durationMinutes: 5 },
];

const CLASS_PERIOD_SWEEP_SECONDS = 15;
const CLASS_PERIOD_COMPLETE_HOLD_SECONDS = 1;
const CLASS_PERIOD_SWEEP_TICK_SECONDS = 0.05;
const CLASS_PERIOD_SWEEP_TICK_MS = CLASS_PERIOD_SWEEP_TICK_SECONDS * 1000;
const CLASS_PERIOD_TOTAL_MINUTES = CLASS_PERIOD_AGENDA_STEPS.reduce(
  (totalMinutes, { durationMinutes }) => totalMinutes + durationMinutes,
  0
);
const SHARE_OBSERVATIONS_STEP_INDEX = CLASS_PERIOD_AGENDA_STEPS.findIndex(({ title }) => title === "Share Observations");
const PARTNER_LAB_STEP_INDEX = CLASS_PERIOD_AGENDA_STEPS.findIndex(({ title }) => title === "Partner Lab");
const SHARE_OBSERVATIONS_START_MINUTES = CLASS_PERIOD_AGENDA_STEPS
  .slice(0, SHARE_OBSERVATIONS_STEP_INDEX)
  .reduce((totalMinutes, { durationMinutes }) => totalMinutes + durationMinutes, 0);
const SHARE_OBSERVATIONS_END_MINUTES = SHARE_OBSERVATIONS_START_MINUTES
  + CLASS_PERIOD_AGENDA_STEPS[SHARE_OBSERVATIONS_STEP_INDEX].durationMinutes;
const PARTNER_LAB_START_MINUTES = CLASS_PERIOD_AGENDA_STEPS
  .slice(0, PARTNER_LAB_STEP_INDEX)
  .reduce((totalMinutes, { durationMinutes }) => totalMinutes + durationMinutes, 0);
const PARTNER_LAB_END_MINUTES = PARTNER_LAB_START_MINUTES
  + CLASS_PERIOD_AGENDA_STEPS[PARTNER_LAB_STEP_INDEX].durationMinutes;
const SOUND_METER_PERCENT_FRAMES = [62, 66, 59, 71, 64, 68];
const STATIC_SOUND_METER_PERCENT = SOUND_METER_PERCENT_FRAMES[0];
const SOUND_METER_WAVE_BAR_COUNT = 12;
const SOUND_METER_WAVE_HEIGHTS = [34, 58, 44, 76, 62, 92, 70, 48, 66, 40, 74, 52].slice(0, SOUND_METER_WAVE_BAR_COUNT);

const WARMUP_STEP_DURATION_SECONDS = CLASS_PERIOD_AGENDA_STEPS[0].durationMinutes * 60;
const PARTNER_LAB_AGENDA_STEP = CLASS_PERIOD_AGENDA_STEPS[PARTNER_LAB_STEP_INDEX];
const PARTNER_LAB_DURATION_SECONDS = PARTNER_LAB_AGENDA_STEP.durationMinutes * 60;
const SETUP_MATERIALS_DURATION_SECONDS = PARTNER_LAB_AGENDA_STEP.subSteps[0].durationMinutes * 60;

const HERO_AGENDA_WARMUP_INTRO_SECONDS = 2;

const AUTOMATION_MOCK_STATES = [
  {
    scheduleValue: "A Day",
    marqueeText: "Good morning! Check the board, find your seat, and start the warm-up.",
    cueLabel: "Class Change",
    cueTitle: "Science opens",
    cueBody: "Today's schedule, message, and agenda are already on the board.",
    cueMeta: "9:00 AM",
    clockStartSeconds: (8 * 60 * 60) + (59 * 60) + 58,
    clockLoopSeconds: 11,
    clockTimeline: [
      { startTick: 0, endTick: 7, startOffsetSeconds: 0, endOffsetSeconds: 7 },
      { startTick: 7, endTick: 10, startOffsetSeconds: 7, endOffsetSeconds: 30 },
      { startTick: 10, endTick: 11, startOffsetSeconds: 30, endOffsetSeconds: 30 },
    ],
  },
  {
    scheduleValue: "A Day",
    marqueeText: "Welcome music is playing while students settle in.",
    cueLabel: "Auto Music",
    cueTitle: "Welcome track",
    cueBody: "Music starts automatically for the first 3 minutes of class.",
    cueMeta: "9:00:30",
    clockStartSeconds: (9 * 60 * 60) + 28,
    clockLoopSeconds: 16,
    clockTimeline: [
      { startTick: 0, endTick: 12, startOffsetSeconds: 0, endOffsetSeconds: 12 },
      { startTick: 12, endTick: 15, startOffsetSeconds: 12, endOffsetSeconds: 30 },
      { startTick: 15, endTick: 16, startOffsetSeconds: 30, endOffsetSeconds: 30 },
    ],
  },
  {
    scheduleValue: "A Day",
    marqueeText: "Do Now: Answer the warm-up question before the timer ends.",
    cueLabel: "Timer",
    cueTitle: "Warm-up countdown",
    cueBody: "A two-minute timer appears without switching tabs.",
    cueMeta: "2:00",
    clockStartSeconds: (9 * 60 * 60) + 58,
    clockLoopSeconds: 15,
    clockTimeline: [
      { startTick: 0, endTick: 12, startOffsetSeconds: 0, endOffsetSeconds: 12 },
      { startTick: 12, endTick: 14, startOffsetSeconds: 12, endOffsetSeconds: 120 },
      { startTick: 14, endTick: 15, startOffsetSeconds: 120, endOffsetSeconds: 120 },
    ],
  },
  {
    scheduleValue: "A Day",
    marqueeText: "Don't forget to take attendance ;)",
    cueLabel: "Reminder",
    cueTitle: "Attendance reminder",
    cueBody: "Scheduled reminders appear on your display at the right time.",
    cueMeta: "Done",
    clockStartSeconds: (9 * 60 * 60) + (2 * 60) + 58,
    clockLoopSeconds: 9,
    clockTimeline: [
      { startTick: 0, endTick: 2, startOffsetSeconds: 0, endOffsetSeconds: 2 },
      { startTick: 2, endTick: 7, startOffsetSeconds: 2, endOffsetSeconds: 7 },
      { startTick: 7, endTick: 9, startOffsetSeconds: 7, endOffsetSeconds: 60 },
    ],
  },
  {
    scheduleValue: "A Day",
    marqueeText: "Happy Birthday, Avery! We hope you have a great day!",
    cueLabel: "Birthday",
    cueTitle: "Birthday note",
    cueBody: "Scheduled celebrations show up at the right moment.",
    cueMeta: "9:04",
    clockStartSeconds: (9 * 60 * 60) + (3 * 60) + 58,
    clockLoopSeconds: 9,
    clockTimeline: [
      { startTick: 0, endTick: 2, startOffsetSeconds: 0, endOffsetSeconds: 2 },
      { startTick: 2, endTick: 7, startOffsetSeconds: 2, endOffsetSeconds: 7 },
      { startTick: 7, endTick: 9, startOffsetSeconds: 7, endOffsetSeconds: 60 },
    ],
  },
  {
    scheduleValue: "A Day",
    marqueeText: "Partner lab is underway. Follow the agenda and watch your time.",
    cueLabel: "Agenda",
    cueTitle: "Agenda advances",
    cueBody: "The active step and sub-step progress through the lesson.",
    cueMeta: "Live",
    clockStartSeconds: (9 * 60 * 60) + (5 * 60),
    clockLoopSeconds: CLASS_PERIOD_SWEEP_SECONDS + CLASS_PERIOD_COMPLETE_HOLD_SECONDS,
    clockSpanSeconds: CLASS_PERIOD_TOTAL_MINUTES * 60,
    clockSweepSeconds: CLASS_PERIOD_SWEEP_SECONDS,
  },
  {
    scheduleValue: "A Day",
    marqueeText: "Cleanup starts now. Return materials and reset your table.",
    cueLabel: "Cleanup",
    cueTitle: "Cleanup timer",
    cueBody: "A final countdown starts with two minutes left.",
    cueMeta: "2:00",
    clockStartSeconds: (9 * 60 * 60) + (43 * 60),
    clockLoopSeconds: 13,
    clockTimeline: [
      { startTick: 0, endTick: 10, startOffsetSeconds: 0, endOffsetSeconds: 10 },
      { startTick: 10, endTick: 12, startOffsetSeconds: 10, endOffsetSeconds: 120 },
      { startTick: 12, endTick: 13, startOffsetSeconds: 120, endOffsetSeconds: 120 },
    ],
  },
  {
    scheduleValue: "A Day",
    marqueeText: "Next class is ready. New message, agenda, and tabs are loaded.",
    cueLabel: "Next Class",
    cueTitle: "3rd period loads",
    cueBody: "The display changes for the next science period automatically.",
    cueMeta: "9:45",
    className: "SciencePeriod3",
    clockStartSeconds: (9 * 60 * 60) + (44 * 60) + 58,
    clockLoopSeconds: 3600,
  },
];

const STATIC_WIDGET_PREVIEW_DELAY_SECONDS = 4.2;
const HERO_WIDGET_PREVIEW_DELAY_SECONDS = 0.8;
const TIMER_MOCK_INDEX = AUTOMATION_MOCK_STATES.findIndex(({ cueLabel }) => cueLabel === "Timer");
const AGENDA_PROGRESS_MOCK_INDEX = AUTOMATION_MOCK_STATES.findIndex(({ cueLabel }) => cueLabel === "Agenda");
const AUTO_MUSIC_MOCK_INDEX = AUTOMATION_MOCK_STATES.findIndex(({ cueLabel }) => cueLabel === "Auto Music");
const REMINDER_MOCK_INDEX = AUTOMATION_MOCK_STATES.findIndex(({ cueLabel }) => cueLabel === "Reminder");
const BIRTHDAY_MOCK_INDEX = AUTOMATION_MOCK_STATES.findIndex(({ cueLabel }) => cueLabel === "Birthday");
const CLEANUP_MOCK_INDEX = AUTOMATION_MOCK_STATES.findIndex(({ cueLabel }) => cueLabel === "Cleanup");
const NEXT_CLASS_MOCK_INDEX = AUTOMATION_MOCK_STATES.findIndex(({ cueLabel }) => cueLabel === "Next Class");
const AUTO_MUSIC_PLAYER_DELAY_SECONDS = 2;
const BIRTHDAY_ANNOUNCEMENT_DELAY_SECONDS = 1;
const BIRTHDAY_CONFETTI_DELAY_SECONDS = 1.5;
const CLASS_CHANGE_LABEL_SECONDS = 1;
const CLASS_CHANGE_COUNTDOWN_SECONDS = 3;
const CLASS_CHANGE_AT_SECONDS = CLASS_CHANGE_LABEL_SECONDS + CLASS_CHANGE_COUNTDOWN_SECONDS;
const TIMER_POPUP_DURATION_SECONDS = 120;
const WARMUP_TIMER_START_OFFSET_SECONDS = 12;
const WARMUP_TIMER_END_MESSAGE = "Time's up!";
const CLEANUP_TIMER_END_MESSAGE = "See you tomorrow!";

const formatClockTime = (date) =>
  date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).replace(/\s?[AP]M$/i, "");

const formatClockSeconds = (totalSeconds) => {
  const secondsInDay = 24 * 60 * 60;
  const normalizedSeconds = ((totalSeconds % secondsInDay) + secondsInDay) % secondsInDay;
  const hours24 = Math.floor(normalizedSeconds / 3600);
  const minutes = Math.floor((normalizedSeconds % 3600) / 60);
  const seconds = normalizedSeconds % 60;
  const hours12 = hours24 % 12 || 12;

  return `${hours12}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

const getTimelineClockOffsetSeconds = (cue, elapsedSeconds) => {
  if (cue.clockTimeline) {
    const activeSegment = cue.clockTimeline.find(({ startTick, endTick }) => (
      elapsedSeconds >= startTick && elapsedSeconds < endTick
    )) || cue.clockTimeline[cue.clockTimeline.length - 1];
    const segmentDurationSeconds = Math.max(1, activeSegment.endTick - activeSegment.startTick);
    const segmentElapsedSeconds = Math.max(0, Math.min(segmentDurationSeconds, elapsedSeconds - activeSegment.startTick));
    const segmentProgress = segmentElapsedSeconds / segmentDurationSeconds;

    return Math.floor(
      activeSegment.startOffsetSeconds
      + ((activeSegment.endOffsetSeconds - activeSegment.startOffsetSeconds) * segmentProgress)
    );
  }

  if (!cue.clockSpanSeconds) {
    return Math.floor(elapsedSeconds);
  }

  const sweepSeconds = cue.clockSweepSeconds || cue.clockLoopSeconds || 1;
  const sweepProgress = Math.min(elapsedSeconds, sweepSeconds) / sweepSeconds;

  return Math.floor(sweepProgress * cue.clockSpanSeconds);
};

const isClockFastForwarding = (cue, elapsedSeconds) => {
  if (cue.clockSpanSeconds) {
    return elapsedSeconds < (cue.clockSweepSeconds || cue.clockLoopSeconds || 0);
  }

  if (!cue.clockTimeline) {
    return false;
  }

  const activeSegment = cue.clockTimeline.find(({ startTick, endTick }) => (
    elapsedSeconds >= startTick && elapsedSeconds < endTick
  ));

  if (!activeSegment) {
    return false;
  }

  const tickSpan = Math.max(1, activeSegment.endTick - activeSegment.startTick);
  const clockSpan = Math.abs(activeSegment.endOffsetSeconds - activeSegment.startOffsetSeconds);

  return clockSpan / tickSpan > 2;
};

const formatAgendaCountdown = (totalSeconds) => {
  const safeSeconds = Math.max(0, Math.ceil(totalSeconds));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const getCountdownTimerRemaining = (elapsedSeconds) => (
  formatAgendaCountdown(TIMER_POPUP_DURATION_SECONDS - Math.max(0, Math.floor(elapsedSeconds)))
);

const getAgendaRuntimeFromStart = (durationSeconds, elapsedSeconds) => {
  const elapsedInStep = Math.max(0, Math.min(durationSeconds, Math.floor(elapsedSeconds)));
  const remainingSeconds = Math.max(0, durationSeconds - elapsedInStep);

  return {
    progress: durationSeconds === 0
      ? 0
      : Math.max(0, Math.min(100, (elapsedInStep / durationSeconds) * 100)),
    remaining: formatAgendaCountdown(remainingSeconds),
  };
};

const getRuntimeFromClassMinutes = (elapsedClassMinutes, startMinutes, endMinutes) => {
  const durationMinutes = Math.max(1, endMinutes - startMinutes);
  const elapsedInStepMinutes = Math.max(0, Math.min(durationMinutes, elapsedClassMinutes - startMinutes));
  const remainingSeconds = Math.max(0, Math.ceil((durationMinutes - elapsedInStepMinutes) * 60));

  return {
    progress: Math.max(0, Math.min(100, (elapsedInStepMinutes / durationMinutes) * 100)),
    remaining: formatAgendaCountdown(remainingSeconds),
  };
};

const getDefaultAgendaSteps = (agendaTick) => {
  const activeStepRuntime = getAgendaRuntimeFromStart(PARTNER_LAB_DURATION_SECONDS, agendaTick);
  const activeSubStepRuntime = getAgendaRuntimeFromStart(SETUP_MATERIALS_DURATION_SECONDS, agendaTick);

  return AGENDA_STEPS.map((step) => {
    if (step.status !== "active") {
      return step;
    }

    return {
      ...step,
      progressRuntime: activeStepRuntime,
      subSteps: step.subSteps.map((subStep) => (
        subStep.status === "active"
          ? { ...subStep, progressRuntime: activeSubStepRuntime }
          : subStep
      )),
    };
  });
};

const getHeroAgendaProgressSteps = (elapsedSeconds) => {
  if (elapsedSeconds < HERO_AGENDA_WARMUP_INTRO_SECONDS) {
    const remainingSeconds = HERO_AGENDA_WARMUP_INTRO_SECONDS - elapsedSeconds;
    const elapsedInWarmup = WARMUP_STEP_DURATION_SECONDS - remainingSeconds;
    const warmupRuntime = {
      progress: Math.max(0, Math.min(100, (elapsedInWarmup / WARMUP_STEP_DURATION_SECONDS) * 100)),
      remaining: formatAgendaCountdown(remainingSeconds),
    };

    return AGENDA_STEPS.map(({ title }) => (
      title === "Warm-up Question"
        ? { title, status: "active", progressRuntime: warmupRuntime }
        : { title, status: "upcoming" }
    ));
  }

  return getDefaultAgendaSteps(Math.floor(elapsedSeconds - HERO_AGENDA_WARMUP_INTRO_SECONDS));
};

const getIdleAgendaSteps = () => AGENDA_STEPS.map(({ title }) => ({
  title,
  status: "upcoming",
}));

const getPreviousClassAgendaSteps = () => PREVIOUS_CLASS_AGENDA_STEPS;

const getNextClassAgendaSteps = (elapsedSeconds = 0) => {
  const warmupRuntime = getAgendaRuntimeFromStart(WARMUP_STEP_DURATION_SECONDS, elapsedSeconds);

  return NEXT_CLASS_AGENDA_STEP_TITLES.map((title) => (
    title === "Warm-up Question"
      ? { title, status: "active", progressRuntime: warmupRuntime }
      : { title, status: "upcoming" }
  ));
};

const getCompletedAgendaSteps = () => AGENDA_STEPS.map(({ title }) => ({
  title,
  status: "complete",
}));

const getClassPeriodAgendaSteps = (agendaTick) => {
  const cycleSeconds = CLASS_PERIOD_SWEEP_SECONDS + CLASS_PERIOD_COMPLETE_HOLD_SECONDS;
  const elapsedCycleSeconds = agendaTick % cycleSeconds;
  const elapsedClassMinutes = elapsedCycleSeconds >= CLASS_PERIOD_SWEEP_SECONDS
    ? CLASS_PERIOD_TOTAL_MINUTES
    : (elapsedCycleSeconds / CLASS_PERIOD_SWEEP_SECONDS) * CLASS_PERIOD_TOTAL_MINUTES;
  let stepStartMinutes = 0;

  return CLASS_PERIOD_AGENDA_STEPS.map((step) => {
    const startMinutes = stepStartMinutes;
    const endMinutes = startMinutes + step.durationMinutes;
    const status = elapsedClassMinutes >= endMinutes
      ? "complete"
      : elapsedClassMinutes >= startMinutes
        ? "active"
        : "upcoming";
    const stepRuntime = status === "active"
      ? getRuntimeFromClassMinutes(elapsedClassMinutes, startMinutes, endMinutes)
      : null;
    stepStartMinutes = endMinutes;

    return {
      title: step.title,
      status,
      progressRuntime: stepRuntime,
      subSteps: status === "active" && step.subSteps
        ? getClassPeriodSubSteps(step.subSteps, elapsedClassMinutes - startMinutes)
        : undefined,
    };
  });
};

const getClassPeriodSubSteps = (subSteps, elapsedStepMinutes) => {
  let subStepStartMinutes = 0;

  return subSteps.map((subStep) => {
    const startMinutes = subStepStartMinutes;
    const endMinutes = startMinutes + subStep.durationMinutes;
    const status = elapsedStepMinutes >= endMinutes
      ? "complete"
      : elapsedStepMinutes >= startMinutes
        ? "active"
        : "upcoming";
    subStepStartMinutes = endMinutes;

    return {
      title: subStep.title,
      status,
      progressRuntime: status === "active"
        ? getRuntimeFromClassMinutes(elapsedStepMinutes, startMinutes, endMinutes)
        : null,
    };
  });
};

const AgendaProgressMock = ({ progress, remaining, isSubStep = false }) => (
  <div className={`landing-v2-present-agenda-box__progress-row${isSubStep ? " landing-v2-present-agenda-box__progress-row--substep" : ""}`}>
    <div className="landing-v2-present-agenda-box__progress-track" aria-hidden="true">
      <span style={{ width: `${progress}%` }} />
      <i style={{ left: `${progress}%` }} />
    </div>
    <strong>{remaining}</strong>
  </div>
);

const PresentAgendaBoxMock = ({
  title = "2nd Period Agenda",
  isActive = true,
  isComplete = false,
  shouldShowNextClass = false,
  shouldShowPreviousClass = false,
  shouldSweepClassPeriod = false,
  heroAgendaElapsedSeconds,
  nextClassAgendaElapsedSeconds,
}) => {
  const [agendaTick, setAgendaTick] = useState(0);
  const agendaSteps = shouldShowPreviousClass
    ? getPreviousClassAgendaSteps()
    : shouldShowNextClass
      ? getNextClassAgendaSteps(nextClassAgendaElapsedSeconds ?? agendaTick)
    : !isActive
    ? getIdleAgendaSteps()
    : isComplete
      ? getCompletedAgendaSteps()
      : heroAgendaElapsedSeconds !== undefined
        ? getHeroAgendaProgressSteps(heroAgendaElapsedSeconds)
      : shouldSweepClassPeriod
        ? getClassPeriodAgendaSteps(agendaTick)
        : getDefaultAgendaSteps(agendaTick);

  useEffect(() => {
    if (
      !isActive
      || isComplete
      || shouldShowPreviousClass
      || heroAgendaElapsedSeconds !== undefined
      || nextClassAgendaElapsedSeconds !== undefined
    ) {
      setAgendaTick(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setAgendaTick((currentTick) => currentTick + (shouldSweepClassPeriod ? CLASS_PERIOD_SWEEP_TICK_SECONDS : 1));
    }, shouldSweepClassPeriod ? CLASS_PERIOD_SWEEP_TICK_MS : 1000);

    return () => window.clearInterval(intervalId);
  }, [
    isActive,
    isComplete,
    nextClassAgendaElapsedSeconds,
    shouldShowPreviousClass,
    shouldSweepClassPeriod,
  ]);

  return (
    <div className="landing-v2-present-agenda-box">
      <div className="landing-v2-present-agenda-box__card-header">
        <h3>{title}</h3>
      </div>

      <div className="landing-v2-present-agenda-box__body">
        <div className="landing-v2-present-agenda-box__list">
          {agendaSteps.map((step) => (
            <div
              key={step.title}
              className={`landing-v2-present-agenda-box__step landing-v2-present-agenda-box__step--${step.status}`}
            >
              <div className="landing-v2-present-agenda-box__step-row">
                {step.status !== "active" ? (
                  <span className="landing-v2-present-agenda-box__check-slot" aria-hidden="true">
                    {step.status === "complete" ? <i className="fa fa-check" /> : null}
                  </span>
                ) : null}
                <span className="landing-v2-present-agenda-box__step-title">{step.title}</span>
              </div>

              {step.status === "active" ? (
                <>
                  <AgendaProgressMock
                    progress={step.progressRuntime.progress}
                    remaining={step.progressRuntime.remaining}
                  />
                  <div className="landing-v2-present-agenda-box__substeps">
                    {(step.subSteps || []).map((subStep) => (
                      <div
                        key={subStep.title}
                        className={`landing-v2-present-agenda-box__substep landing-v2-present-agenda-box__substep--${subStep.status}`}
                      >
                        <div className="landing-v2-present-agenda-box__substep-row">
                          <span className="landing-v2-present-agenda-box__check-slot" aria-hidden="true">
                            {subStep.status === "complete" ? <i className="fa fa-check" /> : null}
                          </span>
                          <span className="landing-v2-present-agenda-box__substep-title">{subStep.title}</span>
                        </div>
                        {subStep.status === "active" ? (
                          <AgendaProgressMock
                            progress={subStep.progressRuntime.progress}
                            remaining={subStep.progressRuntime.remaining}
                            isSubStep
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const WarmupIllustrationMock = ({ type }) => {
  if (type === "plant-cell") {
    return (
      <div className="landing-v2-present-multi-box__warmup-art landing-v2-present-multi-box__warmup-art--plant-cell" aria-hidden="true">
        <svg viewBox="0 0 132 96" role="img" aria-label="Plant cell diagram">
          <rect x="4" y="4" width="124" height="88" rx="10" fill="#ecfdf3" stroke="#15803d" strokeWidth="4" />
          <rect x="10" y="10" width="112" height="76" rx="8" fill="#f0fdf4" stroke="#86efac" strokeWidth="1.5" />
          <ellipse cx="66" cy="52" rx="34" ry="24" fill="#dbeafe" stroke="#60a5fa" strokeWidth="1.5" />
          <circle cx="78" cy="44" r="10" fill="#ddd6fe" stroke="#8b5cf6" strokeWidth="1.5" />
          <circle cx="78" cy="44" r="4" fill="#7c3aed" />
          <ellipse cx="42" cy="34" rx="9" ry="5" fill="#22c55e" stroke="#15803d" strokeWidth="1.2" transform="rotate(-18 42 34)" />
          <ellipse cx="54" cy="62" rx="8" ry="4.5" fill="#22c55e" stroke="#15803d" strokeWidth="1.2" transform="rotate(24 54 62)" />
          <ellipse cx="88" cy="58" rx="7" ry="4" fill="#22c55e" stroke="#15803d" strokeWidth="1.2" transform="rotate(-12 88 58)" />
          <ellipse cx="34" cy="52" rx="6" ry="3.5" fill="#22c55e" stroke="#15803d" strokeWidth="1.2" transform="rotate(32 34 52)" />
          <ellipse cx="62" cy="28" rx="5" ry="3" fill="#f97316" stroke="#c2410c" strokeWidth="1" />
          <ellipse cx="48" cy="68" rx="4.5" ry="2.8" fill="#f97316" stroke="#c2410c" strokeWidth="1" />
        </svg>
      </div>
    );
  }

  return null;
};

const ClassContentMock = ({ content }) => (
  <div className="landing-v2-present-multi-box" aria-hidden="true">
    <div className="landing-v2-present-multi-box__pane landing-v2-present-multi-box__pane--warmup">
      <div className="landing-v2-present-multi-box__warmup-body">
        <div className="landing-v2-present-multi-box__warmup-copy">
          <div className="landing-v2-present-multi-box__pane-label">{content.warmupTitle}</div>
          <p className="landing-v2-present-multi-box__warmup-prompt">{content.warmupPrompt}</p>
        </div>
        {content.warmupIllustration ? <WarmupIllustrationMock type={content.warmupIllustration} /> : null}
      </div>
    </div>

    <div className="landing-v2-present-multi-box__pane landing-v2-present-multi-box__pane--slides">
      <div className="landing-v2-present-multi-box__slide-stage">
        <div className="landing-v2-present-multi-box__slide-canvas">
          <strong>{content.slideHeadline}</strong>
          <div className="landing-v2-present-multi-box__slide-body">
            <ul className="landing-v2-present-multi-box__slide-bullets">
              {content.slideBullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
            <div className="landing-v2-present-multi-box__slide-visual">
              <span>{content.slideVisual.label}</span>
              <p>{content.slideVisual.caption}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="landing-v2-present-multi-box__slide-footer" aria-hidden="true">
        <span className="landing-v2-present-multi-box__slide-nav" />
        <div className="landing-v2-present-multi-box__slide-dots">
          {Array.from({ length: content.slideCount }, (_, index) => (
            <span
              key={`slide-dot-${index}`}
              className={index + 1 === content.activeSlide ? "is-active" : undefined}
            />
          ))}
        </div>
        <span className="landing-v2-present-multi-box__slide-nav landing-v2-present-multi-box__slide-nav--next" />
      </div>
    </div>
  </div>
);

const CountdownTimerPopupMock = ({ message, remaining, endMessage, isComplete = false }) => (
  <div className={`landing-v2-present-countdown-popup${isComplete ? " landing-v2-present-countdown-popup--complete" : ""}`}>
    <p aria-hidden={isComplete}>{message || "\u00A0"}</p>
    <strong>{isComplete ? endMessage : remaining}</strong>
  </div>
);

const ReminderMock = () => (
  <div className="landing-v2-present-reminder-popup landing-v2-present-reminder-popup--visible">
    <strong>Don&apos;t forget to take attendance ;)</strong>
  </div>
);

const BirthdayAnnouncementMock = ({ isVisible }) => (
  <div className={`landing-v2-present-birthday-popup${isVisible ? " landing-v2-present-birthday-popup--visible" : ""}`}>
    <strong>Happy Birthday, Avery!</strong>
  </div>
);

const ClassChangeCountdownMock = ({ digit }) => (
  <div className="landing-v2-present-class-change-countdown" aria-hidden="true">
    <span key={digit} className="landing-v2-present-class-change-countdown__badge">
      <strong className="landing-v2-present-class-change-countdown__digit">{digit}</strong>
    </span>
  </div>
);

const BirthdayConfettiMock = ({ isAnimationPaused = false }) => {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return undefined;
    }

    const updateSize = () => {
      setSize({
        width: node.offsetWidth,
        height: node.offsetHeight,
      });
    };

    updateSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (isAnimationPaused) {
    return null;
  }

  return (
    <div ref={containerRef} className="landing-v2-present-birthday-confetti" aria-hidden="true">
      {size.width > 0 && size.height > 0 ? (
        <ReactConfetti
          width={size.width}
          height={size.height}
          recycle={false}
          gravity={0.13}
          numberOfPieces={520}
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
        />
      ) : null}
    </div>
  );
};

const AutoMusicPlayerMock = () => (
  <div className="landing-v2-present-music-player" aria-hidden="true">
    <div className="landing-v2-present-music-player__controls">
      <button type="button" tabIndex="-1">
        <i className="fa fa-play" />
      </button>
      <button type="button" tabIndex="-1">
        <i className="fa fa-stop" />
      </button>
    </div>
  </div>
);

const WidgetPopupChromeMock = ({
  title,
  className = "landing-v2-present-widget-popup__chrome",
  actionIcons = ["fa-close"],
}) => (
  <div className={className}>
    <div className="landing-v2-present-widget-popup__drag-handle">
      <img
        src={dragIcon}
        className="landing-v2-present-widget-popup__drag-icon"
        alt=""
        aria-hidden="true"
      />
      <span>{title}</span>
    </div>
    <div className="landing-v2-present-widget-popup__header-actions" aria-hidden="true">
      {actionIcons.map((iconClassName) => (
        <span key={iconClassName} className="landing-v2-present-widget-popup__header-button">
          <i className={`fa ${iconClassName}`} />
        </span>
      ))}
    </div>
  </div>
);

const RandomizerPopupMock = () => (
  <div className="landing-v2-present-randomizer-popup" aria-hidden="true">
    <WidgetPopupChromeMock
      title="Randomizer"
      className="landing-v2-present-randomizer-popup__chrome"
    />
    <div className="landing-v2-present-randomizer-popup__body">
      <div className="landing-v2-present-randomizer-popup__wheel-wrap">
        <div className="landing-v2-present-randomizer-popup__wheel" />
        <div className="landing-v2-present-randomizer-popup__pointer" />
      </div>
      <strong>Avery</strong>
    </div>
  </div>
);

const SoundMeterPopupMock = ({ percent }) => (
  <div className="landing-v2-present-sound-meter-popup" aria-hidden="true">
    <WidgetPopupChromeMock
      title="Sound Meter"
      className="landing-v2-present-sound-meter-popup__chrome"
    />
    <div className="landing-v2-present-sound-meter-popup__body">
      <div className="landing-v2-present-sound-meter-popup__preset">Group Work</div>
      <div className="landing-v2-present-sound-meter-popup__waveform" aria-hidden="true">
        {SOUND_METER_WAVE_HEIGHTS.map((height, index) => (
          <span
            key={`sound-wave-${index}`}
            style={{
              "--mock-wave-height": `${height}%`,
              "--mock-wave-index": index,
            }}
          />
        ))}
      </div>
      <div className="landing-v2-present-sound-meter-popup__percent">{percent}%</div>
    </div>
  </div>
);

const POINT_TRACKER_TEAMS = [
  { name: "Team 1", color: "#FF6B6B" },
  { name: "Team 2", color: "#6BCB77" },
];

const POINT_TRACKER_SCORE_SEQUENCE = [
  { team1: 12, team2: 9, activeTeam: 0 },
  { team1: 13, team2: 9, activeTeam: 0 },
  { team1: 13, team2: 10, activeTeam: 1 },
  { team1: 14, team2: 10, activeTeam: 0 },
  { team1: 14, team2: 11, activeTeam: 1 },
  { team1: 15, team2: 11, activeTeam: 0 },
];

const TimerWidgetPopupMock = () => (
  <div className="landing-v2-present-timer-popup landing-v2-present-widget-popup landing-v2-present-widget-popup--stagger-1" aria-hidden="true">
    <WidgetPopupChromeMock
      title="Timer"
      actionIcons={["fa-magic", "fa-music", "fa-close"]}
    />
    <div className="landing-v2-present-timer-popup__body">
      <div className="landing-v2-present-timer-popup__display">8:00</div>
      <div className="landing-v2-present-timer-popup__progress">
        <span />
      </div>
      <div className="landing-v2-present-timer-popup__actions">
        <span>Start</span>
        <span>Reset</span>
      </div>
    </div>
  </div>
);

const SignalWidgetPopupMock = () => (
  <div className="landing-v2-present-signal-popup landing-v2-present-widget-popup landing-v2-present-widget-popup--stagger-2" aria-hidden="true">
    <WidgetPopupChromeMock title="Signal" />
    <div className="landing-v2-present-signal-popup__body" aria-label="Signal lights cycling">
      <span className="landing-v2-present-signal-popup__light landing-v2-present-signal-popup__light--red" />
      <span className="landing-v2-present-signal-popup__light landing-v2-present-signal-popup__light--yellow" />
      <span className="landing-v2-present-signal-popup__light landing-v2-present-signal-popup__light--green is-active" />
    </div>
  </div>
);

const PointTrackerWidgetPopupMock = ({ isAnimationPaused = false }) => {
  const [scoreStepIndex, setScoreStepIndex] = useState(0);

  useEffect(() => {
    if (isAnimationPaused) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setScoreStepIndex((currentIndex) => (
        currentIndex + 1 >= POINT_TRACKER_SCORE_SEQUENCE.length ? 0 : currentIndex + 1
      ));
    }, 2800);

    return () => window.clearInterval(intervalId);
  }, [isAnimationPaused]);

  const scoreStep = POINT_TRACKER_SCORE_SEQUENCE[scoreStepIndex] || POINT_TRACKER_SCORE_SEQUENCE[0];
  const teamScores = [scoreStep.team1, scoreStep.team2];

  return (
    <div className="landing-v2-present-point-tracker-popup landing-v2-present-widget-popup landing-v2-present-widget-popup--stagger-3" aria-hidden="true">
      <WidgetPopupChromeMock
        title="Point Tracker"
        actionIcons={["fa-paint-brush", "fa-plus", "fa-refresh", "fa-close"]}
      />
      <div className="landing-v2-present-point-tracker-popup__body">
        <div className="landing-v2-present-point-tracker-popup__grid">
          {POINT_TRACKER_TEAMS.map(({ name, color }, teamIndex) => (
            <div
              key={name}
              className={`landing-v2-present-point-tracker-popup__team${
                scoreStep.activeTeam === teamIndex ? " landing-v2-present-point-tracker-popup__team--active" : ""
              }`}
              style={{ "--mock-team-color": color }}
            >
              <div className="landing-v2-present-point-tracker-popup__team-name">{name}</div>
              <div className="landing-v2-present-point-tracker-popup__team-score">{teamScores[teamIndex]}</div>
              <div className="landing-v2-present-point-tracker-popup__team-actions">
                <span>-</span>
                <span>+</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MusicPlayerWidgetPopupMock = () => (
  <div className="landing-v2-present-music-popup landing-v2-present-widget-popup landing-v2-present-widget-popup--stagger-4" aria-hidden="true">
    <WidgetPopupChromeMock title="Music" />
    <div className="landing-v2-present-music-popup__body">
      <div className="landing-v2-present-music-popup__track">
        <div className="landing-v2-present-music-popup__art" aria-hidden="true">
          <i className="fa fa-music" />
        </div>
        <div className="landing-v2-present-music-popup__meta">
          <strong>Sunny Classroom</strong>
          <span>Welcome Mix</span>
        </div>
      </div>
      <div className="landing-v2-present-music-popup__progress" aria-hidden="true">
        <span />
      </div>
      <div className="landing-v2-present-music-popup__controls" aria-hidden="true">
        <span><i className="fa fa-step-backward" /></span>
        <span><i className="fa fa-pause" /></span>
        <span><i className="fa fa-step-forward" /></span>
      </div>
    </div>
  </div>
);

const PresentScreenMockFrame = ({
  variant = "full",
  activeAutomationIndex = 0,
  ariaLabel = "Static mockup of the Lesson Launchpad Present Screen",
  onFastForwardChange,
  isMockAudioPlaying = false,
  isAnimationPaused = false,
  disableFastForwardEffects = false,
  heroPreviewMode,
  heroWidgetLayout = false,
}) => {
  const [now, setNow] = useState(() => new Date());
  const [timelineClockTick, setTimelineClockTick] = useState(0);
  const cue = AUTOMATION_MOCK_STATES[Math.min(activeAutomationIndex, AUTOMATION_MOCK_STATES.length - 1)] || AUTOMATION_MOCK_STATES[0];
  const isTimelineVariant = variant === "timeline";
  const useLiveClock = !isTimelineVariant || disableFastForwardEffects;
  const shouldUseHeroWidgetLayout = disableFastForwardEffects || heroWidgetLayout;
  const hasAgendaActivityStarted = !isTimelineVariant
    || AGENDA_PROGRESS_MOCK_INDEX < 0
    || activeAutomationIndex >= AGENDA_PROGRESS_MOCK_INDEX;
  const hasAgendaActivityFinished = isTimelineVariant
    && AGENDA_PROGRESS_MOCK_INDEX >= 0
    && activeAutomationIndex > AGENDA_PROGRESS_MOCK_INDEX;
  const shouldUseStaticAgendaWidgetPreview = isTimelineVariant
    && disableFastForwardEffects
    && AGENDA_PROGRESS_MOCK_INDEX >= 0
    && activeAutomationIndex === AGENDA_PROGRESS_MOCK_INDEX
    && heroPreviewMode === "widgets";
  const shouldUseHeroAgendaWarmupIntro = isTimelineVariant
    && disableFastForwardEffects
    && AGENDA_PROGRESS_MOCK_INDEX >= 0
    && activeAutomationIndex === AGENDA_PROGRESS_MOCK_INDEX
    && heroPreviewMode !== "widgets";
  const shouldSweepAgendaClassPeriod = isTimelineVariant
    && !disableFastForwardEffects
    && AGENDA_PROGRESS_MOCK_INDEX >= 0
    && activeAutomationIndex === AGENDA_PROGRESS_MOCK_INDEX;
  const timelineClockStartSeconds = cue.clockStartSeconds || 0;
  const timelineClockLoopSeconds = cue.clockLoopSeconds || 7;
  const timelineClockElapsed = timelineClockTick % timelineClockLoopSeconds;
  const elapsedClassMinutes = shouldSweepAgendaClassPeriod
    ? (Math.min(timelineClockElapsed, CLASS_PERIOD_SWEEP_SECONDS) / CLASS_PERIOD_SWEEP_SECONDS) * CLASS_PERIOD_TOTAL_MINUTES
    : 0;
  const widgetPreviewDelaySeconds = disableFastForwardEffects
    ? HERO_WIDGET_PREVIEW_DELAY_SECONDS
    : STATIC_WIDGET_PREVIEW_DELAY_SECONDS;
  const shouldShowStaticWidgets = shouldUseStaticAgendaWidgetPreview
    && timelineClockElapsed >= widgetPreviewDelaySeconds;
  const shouldShowRandomizerPopup = (
    shouldSweepAgendaClassPeriod
    && elapsedClassMinutes >= SHARE_OBSERVATIONS_START_MINUTES
    && elapsedClassMinutes < SHARE_OBSERVATIONS_END_MINUTES
  ) || shouldShowStaticWidgets;
  const shouldShowSoundMeterPopup = (
    shouldSweepAgendaClassPeriod
    && elapsedClassMinutes >= PARTNER_LAB_START_MINUTES
    && elapsedClassMinutes < PARTNER_LAB_END_MINUTES
  ) || shouldShowStaticWidgets;
  const shouldShowAgendaCompanionWidgets = shouldShowStaticWidgets
    || shouldShowRandomizerPopup
    || shouldShowSoundMeterPopup;
  const soundMeterPercent = disableFastForwardEffects
    ? STATIC_SOUND_METER_PERCENT
    : SOUND_METER_PERCENT_FRAMES[
      Math.floor(timelineClockTick / CLASS_PERIOD_SWEEP_TICK_SECONDS) % SOUND_METER_PERCENT_FRAMES.length
    ];
  const shouldShowAutoMusicPlayer = isTimelineVariant
    && AUTO_MUSIC_MOCK_INDEX >= 0
    && activeAutomationIndex === AUTO_MUSIC_MOCK_INDEX
    && timelineClockElapsed >= AUTO_MUSIC_PLAYER_DELAY_SECONDS;
  const isReminderStep = isTimelineVariant
    && REMINDER_MOCK_INDEX >= 0
    && activeAutomationIndex === REMINDER_MOCK_INDEX;
  const isBirthdayStep = isTimelineVariant
    && BIRTHDAY_MOCK_INDEX >= 0
    && activeAutomationIndex === BIRTHDAY_MOCK_INDEX;
  const isCleanupStep = isTimelineVariant
    && CLEANUP_MOCK_INDEX >= 0
    && activeAutomationIndex === CLEANUP_MOCK_INDEX;
  const isNextClassStep = isTimelineVariant
    && NEXT_CLASS_MOCK_INDEX >= 0
    && activeAutomationIndex === NEXT_CLASS_MOCK_INDEX;
  const shouldShowNextClass = isNextClassStep && timelineClockElapsed >= CLASS_CHANGE_AT_SECONDS;
  const shouldShowNextClassPreview = isNextClassStep && timelineClockElapsed < CLASS_CHANGE_AT_SECONDS;
  const shouldShowClassChangeCountdown = isNextClassStep
    && timelineClockElapsed >= CLASS_CHANGE_LABEL_SECONDS
    && timelineClockElapsed < CLASS_CHANGE_AT_SECONDS;
  const classChangeCountdownDigit = shouldShowClassChangeCountdown
    ? 3 - Math.floor(timelineClockElapsed - CLASS_CHANGE_LABEL_SECONDS)
    : null;
  const timelineClockOffsetSeconds = isTimelineVariant
    ? getTimelineClockOffsetSeconds(cue, timelineClockElapsed)
    : 0;
  const isWarmupTimerStep = isTimelineVariant
    && TIMER_MOCK_INDEX >= 0
    && activeAutomationIndex === TIMER_MOCK_INDEX;
  const warmupTimerElapsedSeconds = isWarmupTimerStep
    ? Math.max(0, timelineClockOffsetSeconds - WARMUP_TIMER_START_OFFSET_SECONDS)
    : 0;
  const shouldShowWarmupTimer = isWarmupTimerStep
    && timelineClockOffsetSeconds >= WARMUP_TIMER_START_OFFSET_SECONDS;
  const isWarmupTimerComplete = shouldShowWarmupTimer
    && warmupTimerElapsedSeconds >= TIMER_POPUP_DURATION_SECONDS;
  const warmupTimerRemaining = getCountdownTimerRemaining(warmupTimerElapsedSeconds);
  const cleanupTimerElapsedSeconds = isCleanupStep ? timelineClockOffsetSeconds : 0;
  const isCleanupTimerComplete = isCleanupStep
    && cleanupTimerElapsedSeconds >= TIMER_POPUP_DURATION_SECONDS;
  const cleanupTimerRemaining = getCountdownTimerRemaining(cleanupTimerElapsedSeconds);
  const shouldShowPreviousClass = isTimelineVariant
    && activeAutomationIndex === 0
    && timelineClockTick < 2;
  const shouldFastForwardMarquee = isTimelineVariant
    && !disableFastForwardEffects
    && isClockFastForwarding(cue, timelineClockElapsed);
  const displayCue = shouldShowPreviousClass
    ? PREVIOUS_CLASS_MOCK_STATE
    : shouldShowNextClassPreview
      ? NEXT_CLASS_PREVIEW_MOCK_STATE
      : cue;
  const displayClassName = shouldShowPreviousClass
    ? "Math"
    : shouldShowNextClass
      ? "SciencePeriod3"
      : "Science";
  const displayClassContent = CLASS_CONTENT_MOCK[displayClassName];
  const displayClock = useLiveClock
    ? formatClockTime(now)
    : formatClockSeconds(timelineClockStartSeconds + timelineClockOffsetSeconds);
  const marqueeText = isTimelineVariant ? CLASS_MARQUEE_TEXT[displayClassName] : PRESENT_SCREEN_MOCK.marqueeText;
  const editorTabs = useMemo(
    () => (
      isTimelineVariant
        ? ["Agenda", displayClassContent.secondaryTab]
        : PRESENT_SCREEN_MOCK.editorTabs
    ),
    [displayClassContent.secondaryTab, isTimelineVariant]
  );

  useEffect(() => {
    if (!useLiveClock) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [useLiveClock]);

  useEffect(() => {
    if (!isTimelineVariant) {
      return undefined;
    }

    setTimelineClockTick(0);
  }, [activeAutomationIndex, isTimelineVariant]);

  useEffect(() => {
    if (!isTimelineVariant || isAnimationPaused) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setTimelineClockTick((currentTick) => currentTick + CLASS_PERIOD_SWEEP_TICK_SECONDS);
    }, CLASS_PERIOD_SWEEP_TICK_MS);

    return () => window.clearInterval(intervalId);
  }, [
    activeAutomationIndex,
    isAnimationPaused,
    isTimelineVariant,
  ]);

  useEffect(() => {
    onFastForwardChange?.(shouldFastForwardMarquee);

    return () => {
      onFastForwardChange?.(false);
    };
  }, [onFastForwardChange, shouldFastForwardMarquee]);

  return (
    <div
      className={`landing-v2-present-mock landing-v2-present-mock--${variant}${
        shouldShowPreviousClass ? " landing-v2-present-mock--previous-class" : ""
      }${
        shouldShowNextClass ? " landing-v2-present-mock--next-class" : ""
      }${
        isAnimationPaused ? " landing-v2-present-mock--animations-paused" : ""
      }`}
      aria-label={ariaLabel}
    >
      <aside className="landing-v2-present-mock__sidebar" aria-hidden="true">
        <div className="landing-v2-present-mock__logo">
          <img src={sidebarLogo} alt="" />
        </div>
        {SIDEBAR_ITEMS.map((item) => (
          <span key={item} className="landing-v2-present-mock__sidebar-icon" />
        ))}
      </aside>

      <div className="landing-v2-present-mock__topbar">
        <div className="landing-v2-present-mock__schedule-card">
          <span>{PRESENT_SCREEN_MOCK.scheduleLabel}</span>
          <strong>{isTimelineVariant ? displayCue.scheduleValue : PRESENT_SCREEN_MOCK.scheduleValue}</strong>
        </div>

        <div className="landing-v2-present-mock__marquee-card">
          <div
            className={`landing-v2-present-mock__marquee-track${
              shouldFastForwardMarquee ? " landing-v2-present-mock__marquee-track--fast" : ""
            }`}
          >
            <span>{marqueeText}</span>
            <span aria-hidden="true">{marqueeText}</span>
          </div>
        </div>

        <div className="landing-v2-present-mock__clock-card">{displayClock}</div>
      </div>

      <div className="landing-v2-present-mock__content">
        <div className="landing-v2-present-mock__editor-box landing-v2-present-mock__editor-box--agenda">
          <PresentAgendaBoxMock
            title={displayClassContent.agendaTitle}
            isActive={hasAgendaActivityStarted}
            isComplete={hasAgendaActivityFinished}
            shouldShowNextClass={shouldShowNextClass}
            shouldShowPreviousClass={shouldShowPreviousClass}
            shouldSweepClassPeriod={shouldSweepAgendaClassPeriod}
            heroAgendaElapsedSeconds={shouldUseHeroAgendaWarmupIntro ? timelineClockElapsed : undefined}
            nextClassAgendaElapsedSeconds={
              shouldShowNextClass && disableFastForwardEffects && isNextClassStep
                ? timelineClockElapsed - CLASS_CHANGE_AT_SECONDS
                : undefined
            }
          />
        </div>
        <div className={`landing-v2-present-mock__editor-box${isTimelineVariant ? " landing-v2-present-mock__editor-box--multi-box" : ""}`}>
          {isTimelineVariant ? <ClassContentMock content={displayClassContent} /> : null}
        </div>
      </div>

      <div className="landing-v2-present-mock__bottom-nav">
        <div className="landing-v2-present-mock__actions" aria-hidden="true">
          {ACTION_ICONS.map((icon) => (
            <span key={icon} />
          ))}
        </div>

        <div className="landing-v2-present-mock__tabs">
          {editorTabs.map((tab, index) => (
            <span key={`${tab}-${index}`} aria-hidden="true" />
          ))}
        </div>

        <div className="landing-v2-present-mock__date-control">
          <span className="landing-v2-present-mock__date-arrow" />
          <span className="landing-v2-present-mock__date-copy" />
          <span className="landing-v2-present-mock__date-arrow" />
        </div>
      </div>

      {shouldFastForwardMarquee ? (
        <div className="landing-v2-present-mock__fast-forward" aria-hidden="true">
          <span />
          <span />
        </div>
      ) : null}

      {isTimelineVariant && isMockAudioPlaying ? (
        <div
          className={`landing-v2-present-mock__audio-playing${
            shouldFastForwardMarquee ? " landing-v2-present-mock__audio-playing--with-fast-forward" : ""
          }`}
          aria-hidden="true"
        >
          <i className="fa fa-volume-up" />
        </div>
      ) : null}

      {shouldShowAutoMusicPlayer ? <AutoMusicPlayerMock /> : null}
      {(shouldShowAgendaCompanionWidgets || shouldShowSoundMeterPopup || shouldShowRandomizerPopup) ? (
        <div
          className={`landing-v2-present-mock__widget-layer${
            shouldUseHeroWidgetLayout ? " landing-v2-present-mock__widget-layer--perimeter" : ""
          }`}
          aria-hidden="true"
        >
          {shouldShowAgendaCompanionWidgets ? (
            <>
              <MusicPlayerWidgetPopupMock />
              <PointTrackerWidgetPopupMock isAnimationPaused={isAnimationPaused} />
              <TimerWidgetPopupMock />
              <SignalWidgetPopupMock />
            </>
          ) : null}
          {shouldShowSoundMeterPopup ? <SoundMeterPopupMock percent={soundMeterPercent} /> : null}
          {shouldShowRandomizerPopup ? <RandomizerPopupMock /> : null}
        </div>
      ) : null}

      {isTimelineVariant && isWarmupTimerStep && shouldShowWarmupTimer ? (
        <div
          className="landing-v2-present-automation-overlay landing-v2-present-automation-overlay--modal-backdrop"
          aria-hidden="true"
        >
          <div className="landing-v2-present-automation-overlay__scrim" />
          <CountdownTimerPopupMock
            message="Do Now: Answer the warm-up question"
            endMessage={WARMUP_TIMER_END_MESSAGE}
            remaining={warmupTimerRemaining}
            isComplete={isWarmupTimerComplete}
          />
        </div>
      ) : null}

      {isTimelineVariant && isCleanupStep ? (
        <div
          className="landing-v2-present-automation-overlay landing-v2-present-automation-overlay--modal-backdrop"
          aria-hidden="true"
        >
          <div className="landing-v2-present-automation-overlay__scrim" />
          <CountdownTimerPopupMock
            message="Clean up your area"
            endMessage={CLEANUP_TIMER_END_MESSAGE}
            remaining={cleanupTimerRemaining}
            isComplete={isCleanupTimerComplete}
          />
        </div>
      ) : null}

      {isTimelineVariant && (isReminderStep || isBirthdayStep) ? (
        <div
          className="landing-v2-present-automation-overlay landing-v2-present-automation-overlay--modal-backdrop"
          aria-hidden="true"
        >
          <div className="landing-v2-present-automation-overlay__scrim" />
          {isReminderStep ? <ReminderMock /> : null}
          {isBirthdayStep && timelineClockElapsed >= BIRTHDAY_CONFETTI_DELAY_SECONDS ? (
            <BirthdayConfettiMock isAnimationPaused={isAnimationPaused} />
          ) : null}
          {isBirthdayStep && timelineClockElapsed >= BIRTHDAY_ANNOUNCEMENT_DELAY_SECONDS ? (
            <BirthdayAnnouncementMock isVisible />
          ) : null}
        </div>
      ) : null}

      {shouldShowClassChangeCountdown && classChangeCountdownDigit ? (
        <ClassChangeCountdownMock digit={classChangeCountdownDigit} />
      ) : null}
    </div>
  );
};

export default PresentScreenMockFrame;
